import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { Link, useNavigate } from 'react-router-dom';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { useClickMenuItem } from '../AppHeader/useClickMenuItem';
import { NavMenuItem } from '../../interfaces/Settings/NavMenuItem';
import { settingsAtom } from '../../atoms/Settings';
import { bodyWidth } from '../../utils/Style';
import { useThemeColor } from '../../hooks/useThemeColor';
import { PopularCategory } from '../../interfaces/Category/PopularCategory';
import { getPopularCategories } from '../../api/PopularCategory';
import { useLocalizedText } from '../../hooks/useLocalizedText';

const maxColWidth = 250;

const PopularCategoriesMenu = () => {
  const settings = useRecoilValue(settingsAtom);

  const [popularCategories, setPopularCategories] = useState<PopularCategory[]>(
    [],
  );

  const { secondary } = useThemeColor();

  const navigate = useNavigate();

  useEffect(() => {
    getPopularCategories()
      .then((p) => setPopularCategories(p))
      .catch((err) => toast.error(err.message));
  }, []);

  return popularCategories.length ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography
        sx={{
          color: secondary,
          fontSize: '18px',
          fontWeight: 600,
          mb: 1,
          ...settings.settingsV2?.footerDesktopSettings
            ?.popularCategoriesTitleStyle,
        }}
      >
        {settings.settingsV1.popular_categories_text}
      </Typography>

      {popularCategories.map((category, i) => (
        <Button
          onClick={() => navigate(`/kategori/${category.slug}`)}
          key={i}
          sx={{
            color: secondary,
            fontSize: '14px',
            fontWeight: 600,
            width: 'fit-content',
            p: 0,
            minWidth: 0,
            textTransform: 'none',
            textAlign: 'left',
            ...settings.settingsV2?.footerDesktopSettings
              ?.popularCategoriesItemStyle,
          }}
        >
          {category.name}
        </Button>
      ))}
    </Box>
  ) : null;
};

type FooterMenuProps = {
  title: string;
  menuItems: NavMenuItem[];
};

const FooterMenu = ({ title, menuItems }: FooterMenuProps) => {
  const { secondary } = useThemeColor();

  const settings = useRecoilValue(settingsAtom);

  const onClick = useClickMenuItem();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography
        sx={{
          color: secondary,
          fontSize: '18px',
          fontWeight: 600,
          mb: 1,
          ...settings.settingsV2?.footerDesktopSettings?.menuTitleStyle,
        }}
      >
        {title}
      </Typography>

      {menuItems.map((item, i) => (
        <Button
          onClick={() => onClick(item)}
          key={i}
          sx={{
            color: secondary,
            fontSize: '14px',
            fontWeight: 600,
            width: 'fit-content',
            p: 0,
            minWidth: 0,
            textTransform: 'none',
            textAlign: 'left',
            ...settings.settingsV2?.footerDesktopSettings?.menuItemStyle,
          }}
        >
          {item.name}
        </Button>
      ))}
    </Box>
  );
};

export const FooterDesktop = () => {
  const t = useLocalizedText();

  const settings = useRecoilValue(settingsAtom);

  const { primary, secondary } = useThemeColor();

  const navigateTo = useCallback(
    (url: string) => window.open(url, '_blank'),
    [],
  );
  const navigate = useNavigate();

  const logo = settings.settingsV1.storefront_header_logo;

  const textStyles = {
    color: secondary,
    fontSize: '14px',
    fontWeight: 600,
  };

  const secondAddressLine = [
    settings.storeSettings.store_address_2,
    settings.storeSettings.store_city,
    settings.storeSettings.store_country,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        width: '100%',
        pt: 5,
        pb: 2,
        backgroundColor: primary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        mt: 'auto',
        ...settings.settingsV2?.footerDesktopSettings?.rootStyle,
      }}
    >
      <Box
        width={bodyWidth}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            maxWidth: maxColWidth,
            alignItems: 'start',
          }}
        >
          {logo ? (
            <Box
              onClick={() => navigate('')}
              component='img'
              src={logo}
              sx={{
                cursor: 'pointer',
                height: 80,
                width: 'auto',
                ...settings.settingsV2?.footerDesktopSettings?.logoStyle,
              }}
            />
          ) : (
            <Typography sx={{ color: secondary }}>
              {settings.settingsV1.storefront_navbar_text}
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {!!settings.storeSettings.store_address_1 && (
              <Typography
                sx={{
                  ...textStyles,
                  ...settings.settingsV2?.footerDesktopSettings?.addressStyle,
                }}
              >
                {settings.storeSettings.store_address_1}
              </Typography>
            )}

            {!!secondAddressLine.length && (
              <Typography
                sx={{
                  ...textStyles,
                  ...settings.settingsV2?.footerDesktopSettings?.addressStyle,
                }}
              >
                {secondAddressLine.join(', ')}
              </Typography>
            )}
          </Box>

          <Box>
            {!!settings.storeSettings.store_phone && (
              <Link
                to={`tel:${settings.storeSettings.store_phone}`}
                style={{ textDecoration: 'none' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <PhoneIcon
                    fontSize='small'
                    sx={{
                      color: secondary,
                      ...settings.settingsV2?.footerDesktopSettings
                        ?.phoneIconStyle,
                    }}
                  />
                  <Typography
                    sx={{
                      ...textStyles,
                      ...settings.settingsV2?.footerDesktopSettings?.phoneStyle,
                    }}
                  >
                    {settings.storeSettings.store_phone}
                  </Typography>
                </Box>
              </Link>
            )}

            {!!settings.storeSettings.store_email && (
              <Link
                to={`mailto:${settings.storeSettings.store_email}`}
                style={{ textDecoration: 'none' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <EmailIcon
                    fontSize='small'
                    sx={{
                      color: secondary,
                      ...settings.settingsV2?.footerDesktopSettings
                        ?.emailIconStyle,
                    }}
                  />
                  <Typography
                    sx={{
                      ...textStyles,
                      ...settings.settingsV2?.footerDesktopSettings?.emailStyle,
                    }}
                  >
                    {settings.storeSettings.store_email}
                  </Typography>
                </Box>
              </Link>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            {!!settings.settingsV1.storefront_facebook_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_facebook_link)
                }
              >
                <FacebookIcon
                  sx={{
                    color: secondary,
                    ...settings.settingsV2?.footerDesktopSettings
                      ?.socialMediaStyle,
                  }}
                />
              </IconButton>
            )}

            {!!settings.settingsV1.storefront_twitter_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_twitter_link)
                }
              >
                <XIcon
                  sx={{
                    color: secondary,
                    ...settings.settingsV2?.footerDesktopSettings
                      ?.socialMediaStyle,
                  }}
                />
              </IconButton>
            )}

            {!!settings.settingsV1.storefront_instagram_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_instagram_link)
                }
              >
                <InstagramIcon
                  sx={{
                    color: secondary,
                    ...settings.settingsV2?.footerDesktopSettings
                      ?.socialMediaStyle,
                  }}
                />
              </IconButton>
            )}

            {!!settings.settingsV1.storefront_youtube_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_youtube_link)
                }
              >
                <YouTubeIcon
                  sx={{
                    color: secondary,
                    ...settings.settingsV2?.footerDesktopSettings
                      ?.socialMediaStyle,
                  }}
                />
              </IconButton>
            )}

            {!!settings.settingsV1.storefront_linkedin_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_linkedin_link)
                }
              >
                <LinkedInIcon
                  sx={{
                    color: secondary,
                    ...settings.settingsV2?.footerDesktopSettings
                      ?.socialMediaStyle,
                  }}
                />
              </IconButton>
            )}
          </Box>
        </Box>

        <PopularCategoriesMenu />

        <FooterMenu
          title={settings.settingsV1.storefront_footer_menu_one_title}
          menuItems={settings.footer1}
        />
        <FooterMenu
          title={settings.settingsV1.storefront_footer_menu_two_title}
          menuItems={settings.footer2}
        />
      </Box>

      <Divider
        color={secondary}
        sx={{
          width: bodyWidth,
          ...settings.settingsV2?.footerDesktopSettings?.dividerStyle,
        }}
      />

      <Typography
        sx={{
          ...textStyles,
          ...settings.settingsV2?.footerDesktopSettings?.copyrightStyle,
        }}
      >
        {t('footer.copyright_text')}
      </Typography>
    </Box>
  );
};
